import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { UserPlus, Users, Sparkles, RefreshCw, MessageCircle } from 'lucide-react';
import { tribalSocialAPI } from '../services/api';
import FollowButton from './FollowButton';
import PeacePipeMessage from './PeacePipeMessage';
import { useToast } from '../hooks/use-toast';

const FollowSuggestions = ({ limit = 5, showTitle = true, className = '' }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleUserClick = (userId) => {
    navigate(`/public-profile/${userId}`);
  };

  useEffect(() => {
    loadSuggestions();
  }, [limit]);

  const loadSuggestions = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const response = await tribalSocialAPI.getFollowSuggestions(limit);
      setSuggestions(response.suggestions || []);
    } catch (error) {
      console.error('Error loading follow suggestions:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load follow suggestions",
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadSuggestions(true);
  };

  const handleFollowChange = (userId, isNowFollowing) => {
    if (isNowFollowing) {
      // Remove from suggestions when followed
      setSuggestions(prev => prev.filter(s => s.user_id !== userId));
    }
  };

  const getSuggestionIcon = (type) => {
    switch (type) {
      case 'surname_match':
        return '🧬';
      case 'tribal_region':
        return '🏔️';
      case 'healing_interest':
        return '🌿';
      default:
        return '✨';
    }
  };

  const getSuggestionLabel = (type) => {
    switch (type) {
      case 'surname_match':
        return 'Surname Match';
      case 'tribal_region':
        return 'Tribal Region';
      case 'healing_interest':
        return 'Healing Interest';
      default:
        return 'Suggested';
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        {showTitle && (
          <CardHeader>
            <CardTitle className="flex items-center">
              <Sparkles className="w-5 h-5 mr-2" />
              Suggested Connections
            </CardTitle>
          </CardHeader>
        )}
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center space-x-3 animate-pulse">
                <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded w-16"></div>
                </div>
                <div className="w-16 h-8 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (suggestions.length === 0) {
    return (
      <Card className={className}>
        {showTitle && (
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <Sparkles className="w-5 h-5 mr-2" />
                Suggested Connections
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </CardTitle>
          </CardHeader>
        )}
        <CardContent>
          <div className="text-center py-6">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No suggestions available right now</p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="mt-2"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      {showTitle && (
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <Sparkles className="w-5 h-5 mr-2" />
              Suggested Connections
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </CardTitle>
        </CardHeader>
      )}
      <CardContent>
        <div className="space-y-4">
          {suggestions.map((suggestion) => (
            <div key={suggestion.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <div className="flex items-center space-x-3">
                <Avatar 
                  className="w-12 h-12 cursor-pointer hover:ring-2 hover:ring-amber-300 transition-all"
                  onClick={() => handleUserClick(suggestion.user_id)}
                >
                  <AvatarImage src={suggestion.profile_picture} />
                  <AvatarFallback>
                    {suggestion.tribal_name ? suggestion.tribal_name.charAt(0).toUpperCase() : '?'}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h4 
                      className="font-medium text-gray-900 hover:text-amber-600 cursor-pointer transition-colors"
                      onClick={() => handleUserClick(suggestion.user_id)}
                      title={suggestion.tribal_name || 'Unknown User'}
                    >
                      {suggestion.tribal_name || 'Unknown User'}
                    </h4>
                    <span className="text-lg">
                      {getSuggestionIcon(suggestion.suggestion_type)}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2 mt-1">
                    <Badge variant="outline" className="text-xs">
                      {getSuggestionLabel(suggestion.suggestion_type)}
                    </Badge>
                    {suggestion.match_details?.shared_surname && (
                      <span className="text-xs text-gray-500">
                        Surname: {suggestion.match_details.shared_surname}
                      </span>
                    )}
                    {suggestion.match_details?.shared_region && (
                      <span className="text-xs text-gray-500">
                        {suggestion.match_details.shared_region}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-2">
                <FollowButton
                  userId={suggestion.user_id}
                  followType={suggestion.suggestion_type}
                  size="sm"
                  variant="default"
                  onFollowChange={(isFollowing) => handleFollowChange(suggestion.user_id, isFollowing)}
                />
                <PeacePipeMessage
                  receiverId={suggestion.user_id}
                  receiverName={suggestion.tribal_name}
                  trigger={
                    <Button variant="outline" size="sm">
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                  }
                />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default FollowSuggestions;